import { useLayoutEffect, useState } from 'react'
import { User } from '@highlight/common'
import AuthIpcRenderer from '@renderer/ipc/AuthIpc.renderer'
import { useUser } from './useUser'

export type AuthStatus = 'loading' | 'signedIn' | 'signedOut'

/**
 * The user GlobalState is a de-facto tri-state: undefined until main publishes
 * a user (indeterminate), workos when signed in, and an anonymous supabase
 * user after sign-out or session expiry. Unlike useIsLoggedIn, this keeps
 * "not yet known" distinct from "definitively signed out" so gates don't
 * flash before auth hydrates.
 */
export const authStatusFromUser = (user: User | undefined): AuthStatus =>
  user === undefined ? 'loading' : user.provider === 'workos' ? 'signedIn' : 'signedOut'

export const useAuthStatus = (): AuthStatus => {
  const user = useUser()
  const [fallbackStatus, setFallbackStatus] = useState<AuthStatus>('loading')

  // The user state alone can stall at undefined: a signed-out cold start only
  // publishes a user once the anonymous Supabase session is established, which
  // can fail (offline, auth outage). getAuthProvider keys off the durable
  // WorkOS refresh token instead (same as login-required-gate), so signed-out
  // still resolves. Only 'signedOut' is taken from it — with a token present
  // the profile publish is imminent, and 'loading' stays honest until then.
  useLayoutEffect(() => {
    if (user !== undefined) {
      return
    }
    let cancelled = false
    AuthIpcRenderer.getAuthProvider()
      .then((provider) => {
        if (!cancelled && provider !== 'workos') {
          setFallbackStatus('signedOut')
        }
      })
      .catch((error) => {
        console.error('Failed to check auth provider:', error)
      })
    return () => {
      cancelled = true
    }
  }, [user])

  return user !== undefined ? authStatusFromUser(user) : fallbackStatus
}
